import { createApp } from 'vue'
import { createPinia } from 'pinia'
import 'vuetify/dist/vuetify.min.css'

import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'

import * as Sentry from '@sentry/vue'
// import { SpeckleApp } from '@rhdhv/vue-speckle'
import { UserManagementApp } from '@haskoning/vue-user-management'
import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'
import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'

import HeaderImage from '/header5.jpeg'
import HeaderImageLazy from '/header5_lazy.jpg'
import apiUrls from '@/api/config/apiUrls'

import { useDjangoAuthStore } from '@/stores/authDjango'
import { useApi } from '@/api/config/useApi'
import { useAuthorizationStore } from '@/stores/authorization'

const pinia = createPinia()

const { api } = useApi()
const { bus } = useEventsBus()

const authStore = useDjangoAuthStore(pinia)

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})

const app = createApp(App).use(router).use(vuetify).use(pinia)

app
  .use(AuthenticationManagement, {
    appTitle: 'MOOR²',
    headerImage: HeaderImage,
    headerImageLazy: HeaderImageLazy,
    feathers: api,
    authStore: authStore,
    showUsernameAndPasswordLogin: false,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL,
    showForgotPassword: true,
    signInWithAccount: false
  })
  .provide('useAuthStore', authStore)
  .provide('CustomLogInButton', null)

// app.use(SpeckleApp, {
//   VITE_APP_SPECKLE_ID: import.meta.env.VITE_APP_SPECKLE_ID,
//   VITE_APP_SPECKLE_SECRET: import.meta.env.VITE_APP_SPECKLE_SECRET,
//   VITE_APP_SERVER_URL: import.meta.env.VITE_APP_SERVER_URL,
//   VITE_APP_SPECKLE_NAME: import.meta.env.VITE_APP_SPECKLE_NAME,
//   bus: bus
// })

app.use(ComponentLibrary, { externalBus: bus })

app.use(UserManagementApp, {
  authorizationStore: useAuthorizationStore(),
  authStore: authStore
})


Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.mount('#app')

router.isReady().then(() => {

  const feedback = Sentry.getFeedback()
  if (!feedback) return

  let widget: ReturnType<typeof feedback.createWidget> | null = null

  const isAuthenticated = () => {
    return authStore.user !== null
  }

  const updateFeedbackWidget = () => {
    if (isAuthenticated() && !widget) {
      widget = feedback.createWidget()
      widget.appendToDom()
      if (authStore.user?.email) {
        Sentry.setUser({
          username: authStore.user.first_name ?? '',
          email: authStore.user.email ?? ''
        })
      }
    } else if (!isAuthenticated() && widget) {
      widget.removeFromDom()
      widget = null
      Sentry.setUser(null)
    }
  }
  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
})

export default app
